import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';

const NotFound = () => {
  // Create star animation effect
  useEffect(() => {
    const createStar = () => {
      const container = document.querySelector('.stars-container');
      if (!container) return;
      
      const star = document.createElement('div');
      star.classList.add('star');
      
      // Random position
      const posX = Math.random() * 100;
      const posY = Math.random() * 100;
      
      // Random size
      const size = Math.random() * 4 + 1;
      
      // Random opacity and animation duration
      const opacity = Math.random() * 0.5 + 0.3;
      const duration = Math.random() * 5 + 3;
      
      star.style.left = `${posX}%`;
      star.style.top = `${posY}%`;
      star.style.width = `${size}px`;
      star.style.height = `${size}px`;
      star.style.opacity = opacity;
      star.style.animationDuration = `${duration}s`;
      
      container.appendChild(star);
      
      // Remove star after animation to prevent memory leak
      setTimeout(() => {
        star.remove();
      }, duration * 1000);
    };
    
    // Create initial stars
    const totalStars = 100;
    for (let i = 0; i < totalStars; i++) {
      setTimeout(createStar, Math.random() * 3000);
    }
    
    // Create new stars periodically
    const interval = setInterval(createStar, 300);
    
    return () => {
      clearInterval(interval);
    };
  }, []);
  
  return (
    <div className="relative min-h-screen overflow-hidden bg-gradient-to-b from-navy-950 via-blue-950 to-purple-950 text-white flex flex-col items-center justify-center px-4">
      {/* Stars background */}
      <div className="stars-container absolute inset-0 z-0"></div>
      
      {/* Orbiting planet */}
      <div className="absolute w-32 h-32 rounded-full bg-gradient-to-br from-blue-300 to-blue-600 opacity-70 z-0 animate-orbit-slow blur-sm"></div>
      
      {/* Content */}
      <div className="z-10 max-w-xl w-full text-center">
        {/* 404 Text with glitch effect */}
        <h1 className="glitch-text text-9xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-blue-400 via-blue-300 to-indigo-400 mb-2">404</h1>
        
        {/* Animated portal */}
        <div className="relative mx-auto w-40 h-40 mb-6">
          <div className="absolute inset-0 rounded-full bg-gradient-to-r from-blue-500 via-indigo-500 to-purple-500 animate-portal-outer blur-md"></div>
          <div className="absolute inset-4 rounded-full bg-gradient-to-r from-indigo-600 via-purple-600 to-blue-600 animate-portal-middle"></div>
          <div className="absolute inset-8 rounded-full bg-gradient-to-r from-blue-900 via-indigo-900 to-navy-950 animate-portal-inner"></div>
          <div className="absolute inset-12 rounded-full bg-navy-950"></div>
        </div>
        
        <h2 className="text-3xl font-bold text-white mb-2">Lost in Space</h2>
        <p className="text-slate-300 mb-8 max-w-md mx-auto">
          The page you're looking for has drifted into another dimension. Our astronauts are working on bringing it back.
        </p>
        
        <div className="flex flex-col sm:flex-row gap-4 justify-center">
          <Link 
            to="/" 
            className="relative px-6 py-3 bg-gradient-to-r from-blue-600 to-indigo-600 text-white rounded-lg font-medium hover:from-blue-500 hover:to-indigo-500 transition-all duration-300 shadow-lg shadow-blue-600/20 overflow-hidden group"
          >
            <span className="relative z-10">Return to Earth</span>
            <span className="absolute inset-0 bg-gradient-to-r from-blue-400 to-indigo-400 opacity-0 group-hover:opacity-100 transition-opacity duration-300 transform scale-x-0 group-hover:scale-x-100 origin-left"></span>
          </Link>
          <Link 
            to="/contact" 
            className="px-6 py-3 bg-slate-800/50 backdrop-blur-sm text-white rounded-lg font-medium hover:bg-slate-700/50 transition-colors border border-slate-700 shadow-lg shadow-slate-900/30"
          >
            Contact Mission Control
          </Link>
        </div>
      </div>
      
      {/* UFO with light beam */}
      <div className="absolute bottom-10 right-10 animate-float hidden lg:block">
        <div className="relative">
          <div className="w-20 h-8 bg-gradient-to-r from-slate-400 to-slate-600 rounded-full relative">
            <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-12 h-4 bg-sky-400/40 rounded-full blur-sm animate-pulse"></div>
          </div>
          <div className="w-10 h-10 bg-slate-800 rounded-full absolute -top-4 left-1/2 transform -translate-x-1/2"></div>
          {/* Light beam */}
          <div className="absolute top-8 left-1/2 transform -translate-x-1/2 w-10 h-40 bg-gradient-to-b from-blue-400/50 to-transparent blur-sm"></div>
        </div>
      </div>
      
      {/* Astronaut */}
      <div className="absolute bottom-10 left-10 animate-float-slow hidden lg:block">
        <div className="relative w-20 h-20">
          <div className="w-10 h-10 bg-slate-300 rounded-full absolute top-0 left-1/2 transform -translate-x-1/2"></div>
          <div className="w-16 h-14 bg-slate-200 absolute top-6 left-1/2 transform -translate-x-1/2 rounded-lg"></div>
          <div className="w-4 h-8 bg-slate-300 absolute top-12 left-0 rounded-full"></div>
          <div className="w-4 h-8 bg-slate-300 absolute top-12 right-0 rounded-full"></div>
          <div className="w-4 h-10 bg-slate-300 absolute top-16 left-3 rounded-full"></div>
          <div className="w-4 h-10 bg-slate-300 absolute top-16 right-3 rounded-full"></div>
          <div className="w-6 h-2 bg-sky-400 absolute top-4 left-1/2 transform -translate-x-1/2 rounded-full"></div>
        </div>
      </div>
      
      {/* Custom CSS for animations */}
      <style jsx>{`
        @keyframes twinkle {
          0%, 100% { opacity: 0.3; }
          50% { opacity: 1; }
        }
        
        @keyframes glitch {
          0%, 100% { transform: translate(0); }
          20% { transform: translate(-2px, 2px); }
          40% { transform: translate(-2px, -2px); }
          60% { transform: translate(2px, 2px); }
          80% { transform: translate(2px, -2px); }
        }
        
        @keyframes float {
          0%, 100% { transform: translateY(0); }
          50% { transform: translateY(-20px); }
        }
        
        @keyframes float-slow {
          0%, 100% { transform: translateY(0) rotate(5deg); }
          50% { transform: translateY(-15px) rotate(-5deg); }
        }
        
        @keyframes portal-spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
        
        @keyframes orbit {
          0% { transform: translateX(-200px) translateY(100px) scale(0.8); }
          25% { transform: translateX(200px) translateY(100px) scale(1); }
          50% { transform: translateX(200px) translateY(-100px) scale(0.8); }
          75% { transform: translateX(-200px) translateY(-100px) scale(1); }
          100% { transform: translateX(-200px) translateY(100px) scale(0.8); }
        }
        
        .star {
          position: absolute;
          background-color: white;
          border-radius: 50%;
          animation: twinkle ease-in-out infinite;
        }
        
        .glitch-text {
          position: relative;
          animation: glitch 5s infinite;
        }
        
        .glitch-text::before,
        .glitch-text::after {
          content: "404";
          position: absolute;
          top: 0;
          left: 0;
          width: 100%;
          height: 100%;
        }
        
        .glitch-text::before {
          color: #ff00ff;
          z-index: -1;
          transform: translateX(-2px);
          animation: glitch 5s infinite;
          animation-delay: 0.1s;
          opacity: 0.5;
          text-shadow: -1px 0 #00ffff;
        }
        
        .glitch-text::after {
          color: #00ffff;
          z-index: -2;
          transform: translateX(2px);
          animation: glitch 3s infinite;
          animation-delay: 0.2s;
          opacity: 0.5;
          text-shadow: 1px 0 #ff00ff;
        }
        
        .animate-float {
          animation: float 6s ease-in-out infinite;
        }
        
        .animate-float-slow {
          animation: float-slow 8s ease-in-out infinite;
        }
        
        .animate-portal-outer {
          animation: portal-spin 8s linear infinite;
        }
        
        .animate-portal-middle {
          animation: portal-spin 12s linear infinite reverse;
        }
        
        .animate-portal-inner {
          animation: portal-spin 6s linear infinite;
        }
        
        .animate-orbit-slow {
          animation: orbit 20s ease-in-out infinite;
        }
      `}</style>
    </div>
  );
};

export default NotFound;